import React, { useState } from 'react';
import { Calendar, MapPin, Building2, Eye, X, Image as ImageIcon, ChevronLeft, ChevronRight, Tag } from 'lucide-react';
import eventsData from '../data/events.json';

function Events() {
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  const openEventModal = (event) => {
    setSelectedEvent(event);
    setActiveImageIndex(0);
  };

  const closeEventModal = () => {
    setSelectedEvent(null);
  };

  const nextImage = () => {
    if (selectedEvent && selectedEvent.images) {
      setActiveImageIndex((prev) => (prev + 1) % selectedEvent.images.length);
    }
  };

  const prevImage = () => {
    if (selectedEvent && selectedEvent.images) {
      setActiveImageIndex((prev) => (prev - 1 + selectedEvent.images.length) % selectedEvent.images.length);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50/50 via-white to-gray-50 py-8 sm:py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Page Header */}
        <div className="text-center mb-10 md:mb-14">
          <span className="inline-block px-4 py-1.5 bg-orange-100 text-orange-700 font-semibold text-sm rounded-full mb-3">
            கட்சி நிகழ்வுகள்
          </span>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-gray-900 tracking-tight mb-4">
            நிகழ்வுகள்
          </h1>
          <div className="w-24 h-1.5 bg-gradient-to-r from-orange-500 to-orange-600 mx-auto mt-4 rounded-full"></div>
        </div>

        {/* Events Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {eventsData.map((event) => (
            <div
              key={event.id}
              className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100 flex flex-col group"
            >
              {/* Event Image Banner */}
              <div className="relative h-60 overflow-hidden bg-gray-100">
                <img
                  src={`/assets/events/${event.banner_image || event.images[0]}`}
                  alt={event.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  onError={(e) => {
                    e.target.src = 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=800&fit=crop';
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                
                {/* Date Badge */}
                <div className="absolute top-4 left-4">
                  <span className="bg-orange-600 text-white text-xs font-bold px-3 py-1.5 rounded-lg shadow-md flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5" />
                    {event.date_display}
                  </span>
                </div>

                {/* Photo Count Badge */}
                {event.images && event.images.length > 0 && (
                  <div className="absolute top-4 right-4">
                    <span className="bg-black/60 backdrop-blur-md text-white text-xs font-medium px-2.5 py-1 rounded-lg flex items-center gap-1">
                      <ImageIcon className="w-3.5 h-3.5" />
                      {event.images.length} படங்கள்
                    </span>
                  </div>
                )}

                {/* Title Overlay on Image */}
                <div className="absolute bottom-4 left-4 right-4 text-white">
                  <h3 className="text-xl font-bold leading-snug drop-shadow-md">
                    {event.title}
                  </h3>
                </div>
              </div>

              {/* Event Info Details */}
              <div className="p-6 flex-1 flex flex-col justify-between space-y-4">
                <div className="space-y-3 text-sm text-gray-700">
                  
                  {/* Constituency & District */}
                  <div className="flex items-start gap-2.5">
                    <Building2 className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-gray-900">{event.constituency}</p>
                      <p className="text-xs text-gray-500">{event.district}</p>
                    </div>
                  </div>

                  {/* Location */}
                  <div className="flex items-start gap-2.5">
                    <MapPin className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{event.location}</span>
                  </div>

                  {/* Description preview */}
                  {event.description && (
                    <p className="text-gray-600 text-sm line-clamp-2 pt-2 border-t border-gray-100">
                      {event.description}
                    </p>
                  )}
                </div>

                {/* Full View Button */}
                <button
                  onClick={() => openEventModal(event)}
                  className="w-full mt-4 py-2.5 px-4 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2 group-hover:gap-3"
                >
                  <Eye className="w-4 h-4" />
                  <span>முழு விபரம் பார்க்க</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Full View Modal */}
      {selectedEvent && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 sm:p-6 overflow-y-auto">
          <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col my-auto relative animate-in fade-in zoom-in duration-200">
            
            {/* Modal Header */}
            <div className="flex items-center justify-between p-5 border-b border-gray-100 bg-gradient-to-r from-orange-500 to-orange-600 text-white">
              <div>
                <span className="text-xs font-semibold uppercase tracking-wider text-orange-100">நிகழ்வு விபரம்</span>
                <h2 className="text-xl sm:text-2xl font-bold">{selectedEvent.title}</h2>
              </div>
              <button
                onClick={closeEventModal}
                className="p-2 rounded-full hover:bg-white/20 transition-colors text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto space-y-6">
              
              {/* Image Carousel / Gallery */}
              {selectedEvent.images && selectedEvent.images.length > 0 && (
                <div className="space-y-4">
                  <div className="relative h-72 sm:h-96 rounded-2xl overflow-hidden bg-gray-900 shadow-md group">
                    <img
                      src={`/assets/events/${selectedEvent.images[activeImageIndex]}`}
                      alt={`${selectedEvent.title} - ${activeImageIndex + 1}`}
                      className="w-full h-full object-contain"
                    />

                    {/* Left/Right Buttons */}
                    {selectedEvent.images.length > 1 && (
                      <>
                        <button
                          onClick={prevImage}
                          className="absolute left-3 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/50 text-white hover:bg-black/80 transition-colors"
                        >
                          <ChevronLeft className="w-6 h-6" />
                        </button>
                        <button
                          onClick={nextImage}
                          className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/50 text-white hover:bg-black/80 transition-colors"
                        >
                          <ChevronRight className="w-6 h-6" />
                        </button>
                      </>
                    )}

                    {/* Image Counter */}
                    <div className="absolute bottom-3 right-3 bg-black/60 text-white text-xs px-3 py-1.5 rounded-full font-medium">
                      {activeImageIndex + 1} / {selectedEvent.images.length}
                    </div>
                  </div>

                  {/* Thumbnail Row */}
                  {selectedEvent.images.length > 1 && (
                    <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
                      {selectedEvent.images.map((img, idx) => (
                        <button
                          key={idx}
                          onClick={() => setActiveImageIndex(idx)}
                          className={`relative h-20 w-28 rounded-xl overflow-hidden flex-shrink-0 border-2 transition-all ${
                            idx === activeImageIndex ? 'border-orange-500 scale-105 shadow-md' : 'border-transparent opacity-60 hover:opacity-100'
                          }`}
                        >
                          <img
                            src={`/assets/events/${img}`}
                            alt="thumbnail"
                            className="w-full h-full object-cover"
                          />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Event Metadata Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-orange-50/60 p-5 rounded-2xl border border-orange-100">
                <div className="flex items-start gap-3">
                  <div className="p-2.5 bg-orange-500 text-white rounded-xl">
                    <Building2 className="w-5 h-5" />
                  </div>
                  <div>
                    <span className="text-xs text-gray-500 font-medium">தொகுதி & மாவட்டம்</span>
                    <p className="font-bold text-gray-900">{selectedEvent.constituency}</p>
                    <p className="text-sm text-gray-700">{selectedEvent.district}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="p-2.5 bg-orange-500 text-white rounded-xl">
                    <Calendar className="w-5 h-5" />
                  </div>
                  <div>
                    <span className="text-xs text-gray-500 font-medium">நாள் & நேரம்</span>
                    <p className="font-bold text-gray-900">{selectedEvent.date_display}</p>
                
                  </div>
                </div>

                <div className="flex items-start gap-3 sm:col-span-2">
                  <div className="p-2.5 bg-red-500 text-white rounded-xl">
                    <MapPin className="w-5 h-5" />
                  </div>
                  <div>
                    <span className="text-xs text-gray-500 font-medium">இடம்</span>
                    <p className="font-bold text-gray-900">{selectedEvent.location}</p>
                  </div>
                </div>
              </div>

              {/* Event Description */}
              {selectedEvent.description && (
                <div className="space-y-2">
                  <h4 className="font-bold text-gray-900 text-lg flex items-center gap-2">
                    <Tag className="w-4 h-4 text-orange-500" />
                    நிகழ்வு பற்றி
                  </h4>
                  <p className="text-gray-700 leading-relaxed bg-gray-50 p-4 rounded-xl text-base">
                    {selectedEvent.description}
                  </p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-gray-100 bg-gray-50 flex justify-end">
              <button
                onClick={closeEventModal}
                className="px-6 py-2.5 bg-gray-800 text-white font-semibold rounded-xl hover:bg-gray-900 transition-colors shadow"
              >
                மூடு
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Events;
